import React, { useEffect, useMemo, useState } from "react";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty, uniq, filter } from "lodash";
import Total from "../Total";
import CustomDrawer from "../../../components/CustomDrawer";
import PermissionsGate from "../../../permissions/PermissionsGate";
import CustomFloatingButton from "../../../components/CustomFloatingButton";
import { validDept, isItemDepartment } from "../../../utils/deptValidator";
import StoreCreditSummary from "../StoreCreditSummary";
import DepartmentSearch from "../DepartmentSearch";
import usePermissions from "../../../hooks/usePermissions";
import { SCOPES } from "../../../permissions/permissions-map";
import useStableCallback from "../../../hooks/useStableCallback";
import useRequest from "../../../hooks/useRequest";
import CommonDialog from "../../../components/CommonDialog";
import {
  showNotifier,
  resetPogFilters,
  setGlobalLoader,
} from "../../../store/globalSlice";
import { useLazyGetTotalQuery } from "../../../services/totalApi";
import { ENTITY_TYPES, getPogValue } from "../../../utils";
import SettingsForm from "../SettingsForm";
import CustomListContainer from "../CustomListContainer";
import CustomDepartment from "../CustomDepartment";
import CustomNoResult from "../CustomNoResult";
import FileExport from "../../../components/FileExport";
import StickyHeader from "../StickyHeader";
import configData from "../../../config";
import {
  prepareInitialData,
  prepareSettingsSaveData,
  prepareCreatePOData,
  prepareSaveDatesData,
  prepareDeleteDatesData,
  postSaveRestSelections,
} from "./prepareData";

function Departments() {
  const dispatch = useDispatch();
  const { requestMaker } = useRequest();
  const { hasPermission } = usePermissions();
  const [data, setData] = useState([]);
  const [selections, setSelections] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [departmentsLoading, setDepartmentsLoading] = useState(true);
  const [getTotal] = useLazyGetTotalQuery();
  const user = useSelector((store) => store.global.user);
  const store = useSelector((store) => store.global.store);
  const departmentFilters = useSelector(
    (store) => store.global.departmentFilters
  );

  useEffect(() => {
    dispatch(resetPogFilters());
    // Trigger search with default values to fetch all depts.
    handleSearch(departmentFilters);
    // eslint-disable-next-line
  }, []);

  const toggleDrawer = useStableCallback((open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setIsDrawerOpen(open);
  });

  const isXDeptPresent = useMemo(() => {
    return !!data.filter((dept) => isItemDepartment(dept)).length;
  }, [data]);

  const handleSelect = useStableCallback((selectedDept, isChecked) => {
    setData((deptList) => {
      const temp = deptList?.map((dept) => {
        if (dept?.DepartmentLetter === selectedDept?.DepartmentLetter) {
          return {
            ...dept,
            selection: isChecked,
          };
        }
        return dept;
      });
      return temp;
    });

    if (isChecked) {
      setSelections((oldVal) => {
        const updatedSelections = uniq([
          ...oldVal,
          selectedDept?.DepartmentLetter,
        ]);
        const _len = isXDeptPresent ? data.length - 1 : data.length;
        setSelectAll(updatedSelections.length === _len);
        return updatedSelections;
      });
    } else {
      setSelections((oldVal) => {
        return filter(
          oldVal,
          (deptChar) => deptChar !== selectedDept?.DepartmentLetter
        );
      });
      setSelectAll(false);
    }
  });

  const isFreezeDepartment =
    !!departmentFilters.skuValue ||
    !!departmentFilters.vendorName ||
    store?.pending ||
    store?.isArchived ||
    store?.isCompleted ||
    store?.dataguideInProgress ||
    !hasPermission({ scopes: [SCOPES.canEdit] });

  const departmentListRenderer = useMemo(() => {
    if (!data.length) {
      return <CustomNoResult loading={departmentsLoading} />;
    }
    let departmentList = [];
    for (let i = 0; i < data?.length; i++) {
      let department = data[i] || {};
      departmentList.push(
        <CustomDepartment
          freezeDept={isFreezeDepartment || !validDept(department)}
          hideSelection={
            !!departmentFilters.skuValue || !!departmentFilters.vendorName
          }
          key={department?.DepartmentLetter}
          data={department}
          handleSelect={handleSelect}
        />
      );
    }
    return departmentList;
  }, [
    data,
    departmentsLoading,
    isFreezeDepartment,
    handleSelect,
    departmentFilters.skuValue,
    departmentFilters.vendorName,
  ]);

  const handleSearch = useStableCallback(
    (filters = {}, searchAction = false) => {
      const pog = getPogValue(
        filters?.planogramValue || filters?._pogName,
        filters?.planogram
      );
      const filterValues = {
        storeId: store.id,
        departmentName: !!filters?.department
          ? filters.department.split("-")[1]
          : null,
        category: filters?.category || null,
        planogramValue: pog || null,
        vendorName: filters?.vendorName || null,
        skuNumber: filters?._sku || null,
      };

      // If search is by POG #/Name using autosuggestion dropdown
      // redirect user to POG screen
      /* if (searchAction && filters?.viewBy === groupBy[3]) {
        return navigate(`/manage-planograms/pogs/${filters?._deptLetter}`);
      } */
      setDepartmentsLoading(true);
      setData([]);
      setOriginalData([]);
      setSelections([]);
      requestMaker({
        method: "post",
        url: configData.GetDeptList,
        data: filterValues,
      })
        .then((res) => {
          const details = typeof res?.data === "object" ? res?.data : [];
          if (details?.length) {
            const updatedData = prepareInitialData(details);
            setData(updatedData);
            setOriginalData(updatedData);
          }
          if (Object.keys(details).length === 0) {
            dispatch(
              showNotifier({
                message: "No details found for the given data.",
                severity: "info",
              })
            );
          }
        })
        .catch((err) => {
          if (err?.response?.status === 404) {
            dispatch(
              showNotifier({
                message: "No details found for the given data.",
                severity: "info",
              })
            );
          } else {
            dispatch(
              showNotifier({
                message: "Something went wrong. Please try again.",
                severity: "error",
              })
            );
          }
        })
        .finally(() => setDepartmentsLoading(false));
    }
  );

  const handleSelectAll = useStableCallback((event) => {
    const checked = event?.target?.checked;
    setData((oldData) => {
      return oldData?.map((dept) => {
        // Only filtered pog set selection value.
        const deptFound = data.find(
          (fp) => fp?.DepartmentLetter === dept?.DepartmentLetter
        );
        if (deptFound && validDept(dept)) {
          checked &&
            setSelections((oldSel) =>
              uniq([...oldSel, dept?.DepartmentLetter])
            );
          return {
            ...dept,
            selection: checked,
          };
        }
        return dept;
      });
    });

    if (!checked) {
      setSelections([]);
    }
    setSelectAll(!selectAll);
  });

  const handleDeptReset = useStableCallback(() => {
    setShowResetDialog(true);
  });

  const handleResetDialogClose = useStableCallback(() => {
    setShowResetDialog(false);
  });

  const handleResetDialogDone = useStableCallback(() => {
    setData(() => originalData);
    setSelectAll(false);
    setSelections([]);
    handleResetDialogClose();
    window.scrollTo(0, 0);
  });

  const handleSettingsSave = useStableCallback((formData) => {
    dispatch(setGlobalLoader(true));
    setDepartmentsLoading(true);
    setData([]);
    setSelectAll(false);
    setSelections([]);
    setOriginalData([]);
    const postData = prepareSettingsSaveData(
      store.id,
      user.email,
      departmentFilters,
      formData,
      selections
    );
    requestMaker({
      method: "post",
      url: configData.SaveDeptList,
      data: postData,
    })
      .then((res) => {
        const details = typeof res?.data === "object" ? res?.data : [];
        if (Object.keys(details).length === 0) {
          dispatch(
            showNotifier({
              message: "Details saved successfully for selected POGs.",
            })
          );
          handleSearch(departmentFilters);
          getTotal({ storeId: store?.id, type: "" });
        } else {
          setDepartmentsLoading(false);
        }
      })
      .catch((err) => {
        if (err?.response?.status === 404) {
          dispatch(
            showNotifier({
              message: "Can not save details for selected POGs.",
              severity: "error",
            })
          );
        } else {
          dispatch(
            showNotifier({
              message: "Something went wrong. Please try again.",
              severity: "error",
            })
          );
        }
        setDepartmentsLoading(false);
      })
      .finally(() => {
        setIsDrawerOpen(false);
        setSelectAll(false);
        dispatch(setGlobalLoader(false));
      });
  });

  const handleSettingsCreatePO = useStableCallback((formData) => {
    dispatch(setGlobalLoader(true));
    setDepartmentsLoading(true);
    const postData = prepareCreatePOData(
      store.id,
      user.email,
      departmentFilters,
      formData,
      selections
    );
    requestMaker({
      method: "post",
      url: configData.SaveDeptPONumber,
      data: postData,
    })
      .then((res) => {
        const updatedData = postSaveRestSelections(data);
        setData(updatedData);
        setSelections([]);
        dispatch(
          showNotifier({
            message: "Saved changes to PO number type.",
          })
        );
      })
      .catch((err) => {
        if (err?.response?.status === 404) {
          dispatch(
            showNotifier({
              message: "Can not generate PO number for selected POGs.",
              severity: "error",
            })
          );
        } else {
          dispatch(
            showNotifier({
              message: "Something went wrong. Please try again.",
              severity: "error",
            })
          );
        }
      })
      .finally(() => {
        setIsDrawerOpen(false);
        setDepartmentsLoading(false);
        setSelectAll(false);
        dispatch(setGlobalLoader(false));
      });
  });

  const handleSettingsSaveDate = useStableCallback((releaseDate) => {
    dispatch(setGlobalLoader(true));
    setDepartmentsLoading(true);
    const postData = prepareSaveDatesData(
      store.id,
      user.email,
      departmentFilters,
      releaseDate,
      selections
    );
    requestMaker({
      method: "post",
      url: configData.SaveDeptReleaseDate,
      data: postData,
    })
      .then((res) => {
        const updatedData = postSaveRestSelections(data);
        setData(updatedData);
        setSelections([]);
        dispatch(
          showNotifier({
            message: "Release Dates saved successfully for selected POGs.",
          })
        );
      })
      .catch((err) => {
        if (err?.response?.status === 404) {
          dispatch(
            showNotifier({
              message: "Can not save the date for selected POGs.",
              severity: "error",
            })
          );
        } else {
          dispatch(
            showNotifier({
              message: "Something went wrong. Please try again.",
              severity: "error",
            })
          );
        }
      })
      .finally(() => {
        setIsDrawerOpen(false);
        setDepartmentsLoading(false);
        setSelectAll(false);
        dispatch(setGlobalLoader(false));
      });
  });

  const handleSettingsDeleteDate = useStableCallback(() => {
    dispatch(setGlobalLoader(true));
    setDepartmentsLoading(true);
    const postData = prepareDeleteDatesData(
      store.id,
      user.email,
      departmentFilters,
      selections
    );
    requestMaker({
      method: "post",
      url: configData.DeleteDeptReleaseDate,
      data: postData,
    })
      .then((res) => {
        const updatedData = postSaveRestSelections(data);
        setData(updatedData);
        setSelections([]);
        dispatch(
          showNotifier({
            message: "Release Dates deleted successfully for selected POGs.",
          })
        );
      })
      .catch((err) => {
        if (err?.response?.status === 404) {
          dispatch(
            showNotifier({
              message: "Can not delete the date for selected POGs.",
              severity: "error",
            })
          );
        } else {
          dispatch(
            showNotifier({
              message: "Something went wrong. Please try again.",
              severity: "error",
            })
          );
        }
      })
      .finally(() => {
        setIsDrawerOpen(false);
        setDepartmentsLoading(false);
        setSelectAll(false);
        dispatch(setGlobalLoader(false));
      });
  });

  const handleSearchReset = useStableCallback((updatedFilters) => {
    setSelections([]);
    setSelectAll(false);
    handleSearch(updatedFilters);
  });

  const hideSettings = useMemo(() => {
    const isSelectedItemFound = data?.find((i) =>
      selections.includes(i?.DepartmentLetter)
    );
    return isEmpty(selections) || !isSelectedItemFound;
  }, [selections, data]);

  return (
    <Container id="page-container" maxWidth={false} sx={{ paddingY: 2 }}>
      <Stack spacing={2}>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <StoreCreditSummary />
        </Box>
        <Total />
        <DepartmentSearch
          handleSearch={handleSearch}
          handleReset={handleSearchReset}
          freeze={departmentsLoading}
        />
        <CustomListContainer
          entityType={ENTITY_TYPES.department}
          isResultPresent={!!data?.length}
          selectAll={data?.length ? selectAll : false}
          handleSelectAll={handleSelectAll}
          disableSelectAll={false}
          resultLoading={departmentsLoading}
          hideSelectAll={data?.length <= 1 || isFreezeDepartment}
          exportButton={<FileExport data={data} type="osoDepartment" />}
          stickyHeader={<StickyHeader entityType={ENTITY_TYPES.department} />}
          resetAction={
            <PermissionsGate scopes={[SCOPES.canEdit]}>
              {!departmentsLoading && !!data?.length && (
                <>
                  <Button
                    variant="outlined"
                    sx={{ alignSelf: "flex-end" }}
                    onClick={handleDeptReset}
                    size="small"
                    disabled={
                      store?.pending ||
                      store?.isArchived ||
                      store?.isCompleted ||
                      store?.dataguideInProgress
                    }
                  >
                    Reset
                  </Button>
                  <CommonDialog
                    open={showResetDialog}
                    title="Reset All Unsaved Departments"
                    content={
                      "Are you sure, you want to reset/revert all unsaved Department changes?"
                    }
                    type="yesNo"
                    handleClose={handleResetDialogClose}
                    handleDone={handleResetDialogDone}
                  />
                </>
              )}
            </PermissionsGate>
          }
        >
          {departmentListRenderer}
        </CustomListContainer>
      </Stack>
      {!hideSettings && <CustomFloatingButton onClick={toggleDrawer(true)} />}
      <CustomDrawer open={isDrawerOpen} onClose={toggleDrawer(false)}>
        <SettingsForm
          entityType={ENTITY_TYPES.department}
          onSave={handleSettingsSave}
          onCreatePONumbers={handleSettingsCreatePO}
          onSaveDates={handleSettingsSaveDate}
          onDeleteDates={handleSettingsDeleteDate}
          onClose={toggleDrawer(false)}
        />
      </CustomDrawer>
    </Container>
  );
}

export default Departments;
